import type {
  CardDto,
  CardGroup,
  CardItem,
  CardTagGroup,
  DiningTableDto,
  KotDto,
  KotLineDto,
  KotPrintDto,
  KotPrintSummary,
  OrderDetailDto,
  OrderLineDto,
  OrderSummaryDto,
  SendResultDto,
  TableKotSummary,
} from './types/dine.types';

/*
 * Null hydration at the API boundary.
 *
 * crs-api serialises every response with `JsonIgnoreCondition.WhenWritingNull`
 * (api-contract.md → Deviations #1), so a nullable field is ABSENT from the
 * JSON rather than `null`. The DTO types in dine.types.ts say `string | null`;
 * these functions make that true: every nullable field named there is set to
 * `null` when the key is missing, and every array defaults to `[]`. Each
 * service applies the matching hydrator on every return path, so nothing
 * above the services ever sees `undefined` where the type says `null`.
 *
 * The domain is tolerant anyway (`== null`, `?? null`); this is belt and braces.
 */

type Nullable<T> = { [K in keyof T]-?: null extends T[K] ? K : never }[keyof T];

/** A shallow copy with every listed key set to `null` when it is `undefined`. */
export function withNulls<T extends object>(obj: T, keys: ReadonlyArray<Nullable<T>>): T {
  const out = { ...obj } as Record<string, unknown>;
  for (const k of keys) if (out[k as string] === undefined) out[k as string] = null;
  return out as T;
}

/* ── lines ────────────────────────────────────────────────────────────── */

const LINE_NULLS: ReadonlyArray<Nullable<OrderLineDto>> = [
  'itemId',
  'variantId',
  'variantName',
  'stationId',
  'stationName',
  'freeTextNote',
  'roundNumber',
  'kotId',
  'kotNumber',
  'openItemReason',
  'approvalStatus',
  'priceSetBy',
  'priceReason',
  'chargeCode',
  'cancelledReason',
  'effectiveMinutesToTable',
];

export function hydrateLine(l: OrderLineDto): OrderLineDto {
  return { ...withNulls(l, LINE_NULLS), addons: l.addons ?? [], notes: l.notes ?? [] };
}

/* ── tickets ──────────────────────────────────────────────────────────── */

const KOT_NULLS: ReadonlyArray<Nullable<KotDto>> = [
  'tableNumber',
  'roomNumber',
  'acceptedAt',
  'readyAt',
  'servedAt',
  'rejectReason',
];
const KOT_LINE_NULLS: ReadonlyArray<Nullable<KotLineDto>> = ['variantName', 'instructions'];

export function hydrateKotLine(l: KotLineDto): KotLineDto {
  return withNulls(l, KOT_LINE_NULLS);
}

const KOT_PRINT_SUMMARY_NULLS: ReadonlyArray<Nullable<KotPrintSummary>> = ['status', 'printerName', 'error'];

/** `print` is null when the API sent none; inside it the omitted nulls come back and the counts default to 0. */
export function hydrateKotPrintSummary(p: KotPrintSummary | null | undefined): KotPrintSummary | null {
  if (!p) return null;
  return {
    ...withNulls(p, KOT_PRINT_SUMMARY_NULLS),
    mode: p.mode ?? 'BROWSER',
    rerouted: p.rerouted ?? false,
    jobCount: p.jobCount ?? 0,
    reprintCount: p.reprintCount ?? 0,
  };
}

export function hydrateKot(k: KotDto): KotDto {
  return {
    ...withNulls(k, KOT_NULLS),
    print: hydrateKotPrintSummary(k.print),
    lines: (k.lines ?? []).map(hydrateKotLine),
  };
}

/** The slim ticket on a table / order summary has no nullable field; only the array needs a default. */
function hydrateTableKots(kots: TableKotSummary[] | undefined): TableKotSummary[] {
  return kots ?? [];
}

const KOT_PRINT_NULLS: ReadonlyArray<Nullable<KotPrintDto>> = [
  'printerName',
  'tableNumber',
  'roomNumber',
  'guestPropertyName',
  'waiterName',
];

export function hydrateKotPrint(p: KotPrintDto): KotPrintDto {
  return {
    ...withNulls(p, KOT_PRINT_NULLS),
    lines: (p.lines ?? []).map((l) => withNulls(l, ['variantName', 'instructions'])),
  };
}

/* ── orders ───────────────────────────────────────────────────────────── */

const SUMMARY_NULLS: ReadonlyArray<Nullable<OrderSummaryDto>> = [
  'tableId',
  'tableNumber',
  'roomNumber',
  'guestPropertyId',
  'guestName',
  'billRequestedAt',
  'waiterName',
];

export function hydrateOrderSummary(o: OrderSummaryDto): OrderSummaryDto {
  return { ...withNulls(o, SUMMARY_NULLS), kots: hydrateTableKots(o.kots) };
}

export function hydrateOrderDetail(o: OrderDetailDto): OrderDetailDto {
  const base = withNulls(o, [...SUMMARY_NULLS, 'check'] as ReadonlyArray<Nullable<OrderDetailDto>>);
  return {
    ...base,
    avoid: o.avoid ?? [],
    lines: (o.lines ?? []).map(hydrateLine),
    kots: (o.kots ?? []).map(hydrateKot),
  };
}

export function hydrateSendResult(r: SendResultDto): SendResultDto {
  return { order: hydrateOrderDetail(r.order), kots: (r.kots ?? []).map(hydrateKot), unrouted: r.unrouted ?? [] };
}

/* ── tables ───────────────────────────────────────────────────────────── */

const TABLE_NULLS: ReadonlyArray<Nullable<DiningTableDto>> = [
  'sectionId',
  'sectionName',
  'orderId',
  'orderNumber',
  'covers',
  'seatedAt',
  'seatedMinutes',
  'billRequestedAt',
  'guestPresence',
  'roomNumber',
  'guestName',
];

export function hydrateTable(t: DiningTableDto): DiningTableDto {
  return { ...withNulls(t, TABLE_NULLS), kots: hydrateTableKots(t.kots) };
}

/* ── the card ─────────────────────────────────────────────────────────── */

const GROUP_NULLS: ReadonlyArray<Nullable<CardGroup>> = ['stationId', 'stationName', 'minutesToTable', 'addonGroupId'];
const ITEM_NULLS: ReadonlyArray<Nullable<CardItem>> = ['stationId', 'stationName', 'coverPhotoPath', 'special'];
const TAG_GROUP_NULLS: ReadonlyArray<Nullable<CardTagGroup>> = ['bindsTo'];

export function hydrateCardItem(m: CardItem): CardItem {
  const base = withNulls(m, ITEM_NULLS);
  return {
    ...base,
    special: base.special ? withNulls(base.special, ['note']) : null,
    variants: m.variants ?? [],
    addons: m.addons ?? [],
    tags: (m.tags ?? []).map((g) => ({ ...g, tags: g.tags ?? [] })),
    pairs: m.pairs ?? [],
  };
}

export function hydrateCard(c: CardDto): CardDto {
  return {
    ...c,
    courses: c.courses ?? [],
    groups: (c.groups ?? []).map((g) => withNulls(g, GROUP_NULLS)),
    items: (c.items ?? []).map(hydrateCardItem),
    tagGroups: (c.tagGroups ?? []).map((g) => ({ ...withNulls(g, TAG_GROUP_NULLS), tags: g.tags ?? [] })),
    quickNotes: c.quickNotes ?? [],
    charges: c.charges ?? [],
    stations: c.stations ?? [],
  };
}
